<template>
  <div class="ma-4">
    <!-- navigation -->
    <top-navigation
      v-if="editMode"
      :route-name="'extraRide'"
      :title="$t('extraRides.editExtraRide')"
      :button-text="$t('extraRides.cancelEditing')"
      :icon="'fa-times'"
      :icon-title="'fa-pencil'"
      color="red"
    />
    <top-navigation
      v-else
      :route-name="'extraRide'"
      :title="$t('extraRides.newExtraRide')"
      :icon-title="'fa-book-open'"
    />

    <v-row class="justify-center" justify="center">
      <v-col class="col-12">
        <v-card outlined>
          <v-form ref="extraRideForm" @submit.prevent="">
            <v-row>
              <!-- center col -->
              <v-col class="col-8 offset-2">
                <v-row>
                  <!-- ascent input fields -->
                  <v-col class="col-6">
                    <v-card flat>
                      <v-card-title
                        class="d-flex justify-space-between"
                        :class="{ disabled: !ascentActive }"
                      >
                        {{ $t('extraRides.ascent') }}
                        <v-switch v-model="ascentActive" color="primary" />
                      </v-card-title>

                      <v-card-text v-if="ascentActive">
                        <!-- ascent date -->
                        <s-date-picker
                          :min="getTodaysDate()"
                          :date.sync="ascentDate"
                          :label="$t('general.date')"
                          :rules="[
                            (v) => !!v || $t('extraRides.ascentDateNotSet'),
                          ]"
                        />

                        <!-- ascent time -->
                        <s-time-picker
                          :time.sync="ascentTime"
                          :show-clock="false"
                          :label="$t('extraRides.ascentTime')"
                          :rules="[
                            (v) => !!v || $t('extraRides.ascentTimeNotSet'),
                            (v) =>
                              (!!v &&
                                v.match(
                                  /(([0][0-9])|([1][0-9])|([2][0-3]))(:[0-5][0-9])/
                                )) ||
                              $t('singleEventView.timeIncomplete'),
                          ]"
                        />
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <!-- descent input fields -->
                  <v-col class="col-6">
                    <v-card flat>
                      <v-card-title
                        class="d-flex justify-space-between"
                        :class="{ disabled: !descentActive }"
                      >
                        {{ $t('extraRides.descent') }}
                        <v-switch v-model="descentActive" color="primary" />
                      </v-card-title>

                      <v-card-text v-if="descentActive">
                        <!-- descent date -->
                        <s-date-picker
                          :min="getTodaysDate()"
                          :date.sync="descentDate"
                          :label="$t('general.date')"
                          :rules="[
                            (v) => !!v || $t('extraRides.descentDateNotSet'),
                          ]"
                        />

                        <!-- descent time -->
                        <s-time-picker
                          :time.sync="descentTime"
                          :label="$t('extraRides.descentTime')"
                          :show-clock="false"
                          :rules="[
                            (v) => !!v || $t('extraRides.descentTimeNotSet'),
                            (v) =>
                              (!!v &&
                                v.match(
                                  /(([0][0-9])|([1][0-9])|([2][0-3]))(:[0-5][0-9])/
                                )) ||
                              $t('singleEventView.timeIncomplete'),
                            (v) =>
                              (!!v && validateTimeInput()) ||
                              $t('extraRides.descDateError'),
                          ]"
                        />
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- comment field -->
                <v-row>
                  <v-col class="col-12">
                    <v-textarea
                      v-model="comment"
                      class="pa-4"
                      outlined
                      :label="$t('extraRides.comment')"
                    />
                  </v-col>
                </v-row>
              </v-col>

              <!-- spacer col -->
              <v-col class="col-2"></v-col>
            </v-row>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
    <!-- end of wrapper card -->

    <v-row>
      <v-col cols="12" class="d-flex justify-lg-space-between mb-7">
        <!-- delete btn -->
        <v-btn
          v-if="editMode"
          color="red"
          style="color: white"
          large
          @click="$refs.confirmDeletionModal.show()"
        >
          <span>
            <i class="fas fa-trash mr-2"></i>
            {{ this.$t('extraRides.deleteExtraRide') }}
          </span>
          <s-modal
            ref="confirmDeletionModal"
            :size="1"
            :headerText="$t('extraRides.confirmDeletion')"
            classHeader="text-center"
            :okText="$t('extraRides.deleteExtraRide')"
            @ok="deleteExtraRide(ride.id)"
          >
          </s-modal>
        </v-btn>

        <v-spacer></v-spacer>

        <!-- save changes btn / add new extra ride btn -->
        <v-btn class="primary" large @click="validateExtraRideData()">
          <span v-if="editMode">
            <i class="fal fa-save mr-2"></i>
            {{ this.$t('extraRides.applyChanges') }}
          </span>
          <span v-else>
            <i class="fal fa-plus mr-2"></i>
            {{ this.$t('extraRides.applyExtraRide') }}
          </span>
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import DateHelper from '@/classes-shared/utils/DateHelper'
import ExtraRides from '@/classes-internal/extraRides/ExtraRides'
import moment from 'moment'
import STimePicker from '../../components-shared/utils/STimePicker'
import SDatePicker from '../../components-shared/utils/SDatePicker'
import TopNavigation from '../../components-shared/menus/TopNavigation'
import SModal from '../../components-shared/utils/SModal.vue'

export default {
  name: 'ExtraRideBooking',
  components: { SModal, TopNavigation, STimePicker, SDatePicker },

  data() {
    return {
      today: DateHelper.shiftLocalToUtcIsoString(new Date()).substr(0, 10),
      ascentDate: null,
      ascentTime: null,
      descentDate: null,
      descentTime: null,
      comment: '',
      ascentActive: true,
      descentActive: true,
      editMode: this.$route.name === 'editExtraRide' ? true : false,
      id:
        this.$route.name === 'editExtraRide'
          ? parseInt(this.$route.params.id)
          : null,
      extraRides: new ExtraRides(),
    }
  },

  watch: {
    // Watchers are only enabled if ascent / ascent & descent forms are active
    ascentDate() {
      if (this.ascentActive) {
        if (!this.editMode) {
          this.descentDate = this.ascentDate
        } else if (this.descentDate < this.ascentDate) {
          this.descentDate = this.ascentDate
          EventBus.$emit('notify', this.$t('extraRides.descDateError'))
        }
      }
    },
    descentDate() {
      if (this.ascentActive && this.descentActive) {
        if (this.descentDate < this.ascentDate) {
          this.descentDate = this.ascentDate
          EventBus.$emit('notify', this.$t('extraRides.descDateError'))
        }
      }
    },
    // Make sure at least one form field is active
    ascentActive() {
      !this.ascentActive && !this.descentActive
        ? (this.descentActive = true)
        : null
    },
    descentActive() {
      !this.ascentActive && !this.descentActive
        ? (this.ascentActive = true)
        : null
    },
  },

  async mounted() {
    if (this.editMode) {
      let rideDataInstance = new ExtraRides()
      let rideData = await rideDataInstance.getExtraRideById(this.id)

      if (rideData.ascentDate) {
        this.ascentDate = DateHelper.shiftLocalToSimpleDateString(
          rideData.ascentDate
        )
        this.ascentTime = moment(rideData.ascentDate).format('HH:mm')
      } else {
        this.ascentActive = false
      }

      if (rideData.descentDate) {
        this.descentDate = DateHelper.shiftLocalToSimpleDateString(
          rideData.descentDate
        )
        this.descentTime = moment(rideData.descentDate).format('HH:mm')
      } else {
        this.descentActive = false
      }
      this.comment = rideData.comment
    } else {
      this.ascentDate = DateHelper.shiftLocalToSimpleDateString(
        new Date(this.$route.params.ascentDate)
      )
    }
  },

  methods: {
    validateTimeInput() {
      if (!this.ascentTime && !this.descentTime) return false

      if (this.ascentActive && this.descentActive) {
        // Descent time can't be set earlier than ascent time if on same day
        if (
          this.ascentTime >= this.descentTime &&
          moment(this.ascentDate).isSame(this.descentDate, 'day')
        ) {
          return false
        }
      }

      return true
    },

    async validateExtraRideData() {
      // Check form to be complete
      if (!this.$refs.extraRideForm.validate()) return

      // Set up params for api
      let ascentDate = this.ascentActive
        ? DateHelper.shiftLocalToUtcIsoString(
            new Date(this.ascentDate + 'T' + this.ascentTime)
          )
        : null
      let descentDate = this.descentActive
        ? DateHelper.shiftLocalToUtcIsoString(
            new Date(this.descentDate + 'T' + this.descentTime)
          )
        : null
      let comment = this.comment

      // Check if at least one date is set
      if (!ascentDate && !descentDate) return

      // Update existing extra ride
      if (this.editMode) {
        await this.extraRides.updateExtraRide(
          this.id,
          ascentDate,
          descentDate,
          comment
        )
      }
      // Create new extra ride
      else {
        await this.extraRides.addExtraRide(ascentDate, descentDate, comment)
      }
      let query = {}
      if (ascentDate) query.ascentDate = ascentDate.substr(0, 10)
      if (descentDate) query.descentDate = descentDate.substr(0, 10)

      this.$router.push({ name: 'extraRide', query: query })
    },

    async deleteExtraRide() {
      await this.extraRides.deleteExtraRide(this.id)
      this.$router.push({ name: 'extraRide' })
    },

    getTodaysDate() {
      return new Date().toISOString().substr(0, 10)
    },
  },
}
</script>
<style lang="scss" scoped>
.disabled {
  color: var(--v-grey-base);
}
</style>
